import { AccountInfo, EventType } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { createContext, memo, PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { handleRedirectPromise, loginRedirect, logoutRedirect, msalInstance } from "../Common/Auth";
import { ajaxWithAuth } from "../Common/Ajax";
import { ErrorMessageBar } from "../Common/ErrorMessageBar";
import SplashScreen from "../Layout/SplashScreen";
import { MessageBarButton } from "@fluentui/react";

interface DataAccessContextValue {
    activeAccount: AccountInfo | null;
    corpAccount: AccountInfo | null;
    includeEudbPortalTelemetry: boolean;
    auth: { success?: boolean; error?: any; } | undefined;
    setActiveAccount: (account: AccountInfo) => void;
}

export const eudbTenantIds = new Set([
    "33e01921-4d64-4f8c-a055-5bdaffd5e33d", // AME
    "975f013f-7f24-47e8-a7d3-abc4752bf346" // PME
])

export const DataAccessContext = createContext<DataAccessContextValue>({
    activeAccount: null,
    corpAccount: null,
    includeEudbPortalTelemetry: false,
    auth: undefined,
    setActiveAccount: null as any
});

export const DataAccessContextProvider = (props: PropsWithChildren<{}>) => {
    const { accounts } = useMsal();
    const [activeAccount, setActiveAccountState] = useState(() => msalInstance.getActiveAccount());
    const [auth, setAuth] = useState<{ success?: boolean; error?: any; }>();
    const [redirectHandled, setRedirectHandled] = useState(false);
    const [authError, setAuthError] = useState<any>();
    useEffect(() => {
        const callbackId = msalInstance.addEventCallback((message) => {
            if (message.eventType === EventType.ACTIVE_ACCOUNT_CHANGED) {
                setActiveAccountState(msalInstance.getActiveAccount());
            }
        });
        return () => {
            callbackId && msalInstance.removeEventCallback(callbackId);
        };
    }, []);
    const setActiveAccount = useCallback((account: AccountInfo) => {
        setActiveAccountState(account);
        msalInstance.setActiveAccount(account);
    }, []);
    // Handle redirect promise once on mount
    useEffect(() => {
        let disposed = false;
        handleRedirectPromise()
            .then(() => {
                if (!disposed) {
                    // After redirect (e.g., adding a new account), re-evaluate the best primary account
                    // using EUDB tenant preference (AME/PME preferred over Corp)
                    const allAccounts = msalInstance.getAllAccounts();
                    if (allAccounts.length > 0) {
                        const bestAccount = allAccounts.find(a => eudbTenantIds.has(a.tenantId)) || allAccounts[0];
                        setActiveAccount(bestAccount);
                    }
                    setRedirectHandled(true);
                }
            })
            .catch((err) => {
                if (!disposed) {
                    console.error("Auth redirect failed:", err);
                    setAuthError(err);
                }
            });
        return () => { disposed = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Once redirect is handled, resolve the active account if none is set
    // Guard: loginRedirectInProgress prevents StrictMode double-fire from calling loginRedirect() twice
    const loginRedirectInProgress = useRef(false);
    useEffect(() => {
        if (!redirectHandled || activeAccount) return;
        const existingActive = msalInstance.getActiveAccount();
        if (existingActive) {
            setActiveAccountState(existingActive);
        } else {
            const newActiveAccount = accounts.find(x => eudbTenantIds.has(x.tenantId)) || accounts[0];
            if (newActiveAccount) {
                setActiveAccount(newActiveAccount);
            } else if (!loginRedirectInProgress.current) {
                loginRedirectInProgress.current = true;
                loginRedirect();
            }
        }
    }, [redirectHandled, activeAccount, accounts, setActiveAccount]);
    const includeEudbPortalTelemetry = useMemo(() => {
        return activeAccount ? eudbTenantIds.has(activeAccount?.tenantId) : false;
    }, [activeAccount]);
    const corpAccount = useMemo(() => {
        return accounts.find(x => x.tenantId === "72f988bf-86f1-41af-91ab-2d7cd011db47") || null;
    }, [accounts]);
    useEffect(() => {
        let disposed = false;
        if (activeAccount && redirectHandled) {
            ajaxWithAuth("/api/checkAccess")
                .then(() => { !disposed && setAuth({ success: true }) })
                .catch(error => {
                    if (!disposed) {
                        setAuth({ error });
                    }
                });
        }

        return () => { disposed = true; };
    }, [activeAccount, redirectHandled]);
    return (
        <DataAccessContext.Provider value={{ activeAccount, corpAccount, includeEudbPortalTelemetry, auth, setActiveAccount }}>
            {authError
                ? <ErrorMessageBar error={authError} />
                : activeAccount && auth
                    ? auth.success
                        ? props.children
                        : <ErrorMessageBar error={auth.error} actions={<SignOutActionButton activeAccount={activeAccount} />} />
                    : <SplashScreen />}
        </DataAccessContext.Provider>
    )
};

const SignOutActionButton = memo((props: { activeAccount: AccountInfo }) => {
    const { activeAccount } = props;
    const onClick = useCallback(() => {
        logoutRedirect(activeAccount);
    }, [activeAccount]);
    return (
        <MessageBarButton onClick={onClick}>Sign out</MessageBarButton>
    );
});
